import React, { useState } from "react";
import emailjs from "@emailjs/browser";

const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

export default function Contact() {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    company: "",
    service: "",
    message: "",
  });

  const [status, setStatus] = useState("idle");

  /*
   * Handle form field changes
   */
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /*
   * Send email using EmailJS
   */
  const sendEmail = async () => {
    if (!SERVICE_ID || !TEMPLATE_ID || !PUBLIC_KEY) {
      alert("Email service is not configured.");
      throw new Error("Missing EmailJS configuration");
    }

    await emailjs.send(
      SERVICE_ID,
      TEMPLATE_ID,
      {
        fullName: formData.fullName,
        email: formData.email,
        company: formData.company,
        service: formData.service,
        message: formData.message,
      },
      PUBLIC_KEY
    );

    setFormData({
      fullName: "",
      email: "",
      company: "",
      service: "",
      message: "",
    });
  };

  /*
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (status !== "idle") {
      return;
    }

    setStatus("sending");

    try {
      await sendEmail();
      setStatus("sent");

      setTimeout(() => {
        setStatus("idle");
      }, 3000);
    } catch (error) {
      console.error("Failed to send inquiry:", error);
      setStatus("idle");
    }
  };

  return (
    <section
      id="contact"
      className="w-full overflow-hidden bg-[#F7F4EE] py-12 sm:py-14 lg:py-16"
    >
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <div className="mx-auto mb-8 max-w-3xl text-center sm:mb-10 lg:mb-12">
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#C8762A] sm:text-[11px]">
            Contact Us
          </p>

          <h2 className="text-[28px] font-bold leading-[1.15] text-[#1A1714] sm:text-4xl md:text-5xl">
            Let's Build Something
            <br />
            <span className="italic text-[#C8762A]">
              Extraordinary
            </span>
          </h2>

          <p className="mx-auto mt-4 max-w-2xl px-2 text-sm leading-6 text-stone-600 sm:text-base sm:leading-7">
            Have an idea, startup or enterprise software project? We'd love to
            discuss it.
          </p>
        </div>

        {/* Main Layout */}
        <div className="grid min-w-0 gap-6 lg:grid-cols-[300px_minmax(0,1fr)]">
          {/* Left Section */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-1">
            {/* Email */}
            <div className="min-w-0 rounded-2xl border border-stone-200 bg-white p-4 shadow-sm sm:p-5">
              <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-[#FBF0E2] sm:h-11 sm:w-11">
                <span className="material-symbols-outlined text-[20px] text-[#C8762A] sm:text-[24px]">
                  mail
                </span>
              </div>

              <h4 className="text-base font-semibold text-[#1A1714] sm:text-lg">
                Email
              </h4>

              <a
                href="mailto:zevixtech@gmail.com"
                className="mt-1 block break-all text-xs leading-5 text-stone-500 transition hover:text-[#C8762A] sm:text-sm"
              >
                zevixtech@gmail.com
              </a>
            </div>

            {/* Phone */}
            <div className="min-w-0 rounded-2xl border border-stone-200 bg-white p-4 shadow-sm sm:p-5">
              <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 sm:h-11 sm:w-11">
                <span className="material-symbols-outlined text-[20px] text-blue-600 sm:text-[24px]">
                  call
                </span>
              </div>

              <h4 className="text-base font-semibold text-[#1A1714] sm:text-lg">
                Phone
              </h4>

              <a
                href="tel:+917340200113"
                className="mt-1 block text-xs leading-5 text-stone-500 transition hover:text-[#C8762A] sm:text-sm"
              >
                +91 73402 00113
              </a>
            </div>

            {/* Availability */}
            <div className="min-w-0 rounded-2xl border border-stone-200 bg-white p-4 shadow-sm sm:p-5">
              <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-purple-100 sm:h-11 sm:w-11">
                <span className="material-symbols-outlined text-[20px] text-purple-600 sm:text-[24px]">
                  language
                </span>
              </div>

              <h4 className="text-base font-semibold text-[#1A1714] sm:text-lg">
                Availability
              </h4>

              <p className="mt-1 text-xs leading-5 text-stone-500 sm:text-sm">
                Worldwide Remote Services
              </p>
            </div>

            {/* Free Consultation */}
            <div className="min-w-0 rounded-2xl bg-[#1A1714] p-4 text-white sm:p-5">
              <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 sm:h-11 sm:w-11">
                <span className="material-symbols-outlined text-[20px] text-[#C8762A] sm:text-[24px]">
                  forum
                </span>
              </div>

              <h4 className="text-base font-bold sm:text-xl">
                Free Consultation
              </h4>

              <p className="mt-2 text-xs leading-5 text-stone-400 sm:text-sm sm:leading-6">
                Get project estimation, architecture planning, technology
                selection and development guidance.
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="min-w-0 rounded-2xl border border-stone-200 bg-white p-5 shadow-lg sm:rounded-3xl sm:p-7 md:p-8">
            <h3 className="mb-5 text-xl font-bold text-[#1A1714] sm:mb-6 sm:text-2xl md:text-3xl">
              Schedule a Consultation
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
              {/* Name and Email */}
              <div className="grid min-w-0 grid-cols-1 gap-4 sm:gap-5 md:grid-cols-2">
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="Full Name"
                  autoComplete="name"
                  required
                  className="min-w-0 w-full rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm text-[#1A1714] outline-none transition placeholder:text-stone-400 focus:border-[#C8762A] focus:ring-1 focus:ring-[#C8762A]/20 sm:text-base"
                />

                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email Address"
                  autoComplete="email"
                  required
                  className="min-w-0 w-full rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm text-[#1A1714] outline-none transition placeholder:text-stone-400 focus:border-[#C8762A] focus:ring-1 focus:ring-[#C8762A]/20 sm:text-base"
                />
              </div>

              {/* Company */}
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder="Company Name"
                autoComplete="organization"
                className="w-full min-w-0 rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm text-[#1A1714] outline-none transition placeholder:text-stone-400 focus:border-[#C8762A] focus:ring-1 focus:ring-[#C8762A]/20 sm:text-base"
              />

              {/* Service */}
              <select
                name="service"
                value={formData.service}
                onChange={handleChange}
                required
                className={`w-full min-w-0 rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-[#C8762A] focus:ring-1 focus:ring-[#C8762A]/20 sm:text-base ${
                  formData.service ? "text-[#1A1714]" : "text-stone-400"
                }`}
              >
                <option value="" disabled>
                  Select Service
                </option>
                <option value="Web Application">Web Application</option>
                <option value="Mobile Application">Mobile Application</option>
                <option value="Custom Software">Custom Software</option>
                <option value="UI / UX Design">UI / UX Design</option>
                <option value="E-commerce Platform">
                  E-commerce Platform
                </option>
                <option value="Cloud & DevOps">Cloud & DevOps</option>
              </select>

              {/* Message */}
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={5}
                placeholder="Tell us about your project..."
                required
                className="w-full min-w-0 resize-none rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm text-[#1A1714] outline-none transition placeholder:text-stone-400 focus:border-[#C8762A] focus:ring-1 focus:ring-[#C8762A]/20 sm:text-base"
              />

              {/* Submit Button */}
              <button
                type="submit"
                disabled={status !== "idle"}
                className="w-full rounded-xl bg-[#C8762A] px-4 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#b96a22] hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-60 sm:text-base lg:hover:-translate-y-0.5"
              >
                {status === "idle" && "Book Free Consultation"}
                {status === "sending" && "Sending..."}
                {status === "sent" && "✓ Inquiry Sent Successfully"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}